import api, { AppDocument, AppDocStatus } from 'api';
import { Colors } from 'styles/variables/colors';
import Skeleton from '@mui/material/Skeleton';
import { Doc } from 'data/constants/Doc';
import Modal from '@mui/material/Modal';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RootState, AppDispatch } from 'redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { loadDocs, deleteDocs } from 'redux/application/documents/documents-middleware';
import { reset as resetDocStore } from 'redux/application/document/document.slice';
import { reset as resetDocsListStore } from 'redux/application/documents/documents-slice';
import { LTHeader, LTItem, LTRow, LTPlaceholder, LTMobileItem } from 'components/common/ListTable';
import { CustomLTRow } from '../../customLTRow/customLTRow';
import FilePicker from 'components/common/FilePicker/FilePicker';
import { SvgIcon } from '@mui/material';
import { BiTrash } from 'react-icons/bi';
import AlertMessage, { AlertObj } from 'components/common/alert';
import { Tooltip } from '@mui/material';
import IconBtn from 'components/common/IconBtn';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';
import { ReactComponent as CrossSimpleIcon } from 'assets/icons/cross-simple.svg';
import DocumentPreview from 'components/common/DocumentPreview/DocumentPreview';
import DocumentExplanationPopover from './DocumentExplanationPopover';
import { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import DeleteDocumentConfirmationModal from '../../DeleteDocumentConfirmationModal';
import { useWindowSize } from 'hooks/useWindowSize';
import * as S from '../../Table.styled';
import { useTranslation } from 'react-i18next';
import FormBuilder from 'govermentForms/formGenerator';
import { StatusColor } from 'data/constants/caseStatus';
import DocumentTableSelector from './DocumentTableSelector';
import { TableSelection } from './DocumentTableSelector';
import { OpenInNew, Visibility } from '@mui/icons-material';
// import { setClientApplicationStatus } from 'api/rest/application';
import UrlDocumentPreview from 'components/v2/common/documentPreview';
import { getExplanationUrl } from 'utils/v2/document';

type CombinedDocType = Doc | AppDocument | null;
type ClientDocument = AppDocument & {
    sample_uri?: string | null;
    template_uri?: string | null;
};
type PreviewFile = {
    fileUrl: string;
    fileName: string;
    title: string;
} | null;

const displayName = (doc: CombinedDocType, candidateName: string) => {
    if (doc) {
        if ('name' in doc) {
            return candidateName + ' - ' + doc.name;
        } else if ('document_name' in doc) {
            return candidateName + ' - ' + doc.document_name;
        }
    }
    return 'No name available';
};

export default function DocumentsB2B() {
    const dispatch = useDispatch<AppDispatch>();
    const { id: candidateID } = useParams();
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const { list: docsList, isLoading: isDocsLoading } = useSelector(
        (state: RootState) => state.documentsApp
    );
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const [isShowingAlert, setShowingAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<AlertObj>({
        type: 'info',
        value: '',
    });
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    const [isDocViewerOpen, setDocViewerOpen] = useState<boolean>(false);
    const [docForPDFViewer, setDocForPDFViewer] = useState<AppDocument | Doc | null>(null);
    const skeletonArray = docsList.length > 0 ? docsList : [1, 2, 3];
    const [deleteDocConfirmationModalOpen, setDeleteDocConfirmationModalOpenClose] =
        useState<boolean>(false);
    const [documentToDelete, setDocumentToDelete] = useState<AppDocument | null>(null);
    const [documentTableSelection, setDocumentTableSelection] = useState<TableSelection>(
        TableSelection.GENERAL
    );
    const { t } = useTranslation();
    const [generalDocs, setGeneralDocs] = useState<ClientDocument[]>([]);
    const [postItaDocs, setPostItaDocs] = useState<ClientDocument[]>([]);
    const [postSubmissionDocs, setPostSubmissionDocs] = useState<ClientDocument[]>([]);
    const [docsForTable, setDocsForTable] = useState<ClientDocument[]>([]);
    const [previewFile, setPreviewFile] = useState<PreviewFile>(null);

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    const getCurrentStatus = (status: AppDocStatus) => {
        let currentStatus = '';
        let color = '';
        let icon = null;
        if (status === AppDocStatus.MISSING) {
            color = StatusColor.red;
            icon = <S.XIcon />;
            currentStatus = `${t('missing', { lng: language })}`;
        } else if (status === AppDocStatus.FIX_REQUIRED) {
            color = StatusColor.orange;
            icon = <S.WarningIcon />;
            currentStatus = `${t('fixRequired', { lng: language })}`;
        } else if (status === AppDocStatus.WAITING_FOR_APPROVAL) {
            color = StatusColor.blue;
            icon = <S.HourglassIcon />;
            currentStatus = `${t('waiting', { lng: language })}`;
        } else if (status === AppDocStatus.SIGNATURE_REQUIRED) {
            color = StatusColor.orange;
            icon = <S.WarningIcon />;
            currentStatus = `${t('signatureRequired', { lng: language })}`;
        } else if (status === AppDocStatus.PREAPPROVED) {
            color = StatusColor.green;
            icon = <S.CheckmarkIcon />;
            currentStatus = `${t('preapproved', { lng: language })}`;
        } else if (status === AppDocStatus.APPROVED) {
            color = StatusColor.green;
            icon = <S.CheckmarkIcon />;
            currentStatus = `${t('approved', { lng: language })}`;
        } else {
            color = StatusColor.orange;
            icon = <S.WarningIcon />;
            currentStatus = status;
        }

        return (
            <S.NewStatus color={color}>
                {icon}
                {currentStatus}
            </S.NewStatus>
        );
    };

    useEffect(() => {
        if (activeApp) {
            dispatch(loadDocs(activeApp.id.toString()));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeApp]);

    useEffect(() => {
        return () => {
            dispatch(resetDocStore());
            dispatch(resetDocsListStore());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateID]);

    useEffect(() => {
        if (!activeApp?.client_status) return;

        let preferred: TableSelection = TableSelection.GENERAL;

        switch (activeApp.client_status) {
            case ClientApplicationStatus.READY_FOR_SUBMISSION:
            case ClientApplicationStatus.AWAITING_LOA:
                preferred = TableSelection.POST_ITA;
                break;

            case ClientApplicationStatus.SUBMITTED:
            case ClientApplicationStatus.VISA_RESULTS:
                preferred = TableSelection.POST_SUBMISSION;
                break;

            default:
                preferred = TableSelection.GENERAL;
        }

        const mapping: Record<TableSelection, ClientDocument[]> = {
            [TableSelection.GENERAL]: generalDocs,
            [TableSelection.POST_ITA]: postItaDocs,
            [TableSelection.POST_SUBMISSION]: postSubmissionDocs,
        };

        const docsForPreferred = mapping[preferred];

        if (docsForPreferred.length === 0) {
            setDocumentTableSelection(TableSelection.GENERAL);
        } else {
            setDocumentTableSelection(preferred);
        }
    }, [activeApp, generalDocs, postItaDocs, postSubmissionDocs]);

    useEffect(() => {
        if (docsList.length > 0 && activeApp) {
            const generalDocuments: ClientDocument[] = [];
            const postItaDocuments: ClientDocument[] = [];
            const postSubmissionDocuments: ClientDocument[] = [];

            docsList.forEach((doc) => {
                if (doc.explanation?.includes(t('postita', { lng: language }))) {
                    postItaDocuments.push(doc);
                } else if (doc.explanation?.includes(t('postsubmission', { lng: language }))) {
                    postSubmissionDocuments.push(doc);
                } else {
                    generalDocuments.push(doc);
                }
            });
            setGeneralDocs(generalDocuments);
            setPostItaDocs(postItaDocuments);
            setPostSubmissionDocs(postSubmissionDocuments);
        }
    }, [docsList, activeApp, language, t]);

    useEffect(() => {
        switch (documentTableSelection) {
            case TableSelection.GENERAL:
                setDocsForTable(generalDocs);
                break;
            case TableSelection.POST_ITA:
                setDocsForTable(postItaDocs);
                break;
            case TableSelection.POST_SUBMISSION:
                setDocsForTable(postSubmissionDocs);
                break;
            default:
                setDocsForTable(generalDocs);
        }
    }, [documentTableSelection, generalDocs, postItaDocs, postSubmissionDocs]);

    const openCloseDocViewerModal = () => {
        setDocViewerOpen((state) => !state);
    };

    const handleDisplayPDFViewer = async (doc: AppDocument) => {
        setDocForPDFViewer(doc);
        openCloseDocViewerModal();
    };
    //la3

    const handleDownloadDoc = async (doc: AppDocument | Doc) => {
        if (activeApp?.id) {
            try {
                console.log('what');
                const resp = await api.documentResource.getBlobDocument(activeApp?.id, doc.id);
                const blob = resp.data;
                const fileURL = URL.createObjectURL(blob);
                const fileName =
                    resp.headers['resource_name'] ||
                    `${displayName(doc, activeApp?.candidate_name ?? '')}.pdf`;
                const anchor = document.createElement('a');
                anchor.href = fileURL;
                anchor.download = fileName;
                anchor.style.display = 'none';
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
                URL.revokeObjectURL(fileURL);
            } catch (err) {
                console.error('Failed to download:', err);
            }
        }
    };

    const handleUploadDoc = async (file: File, doc: AppDocument | Doc) => {
        const formData: FormData = new FormData();
        formData.append('document', file);
        try {
            await api.documentResource.post(doc.id, formData);
            if (activeApp) {
                dispatch(loadDocs(activeApp.id.toString()));
            }
        } catch {
            setAlertMessage({ type: 'error', value: 'Something went wrong.' });
            setShowingAlert(true);
        }
    };

    const handleReuploadDoc = async (file: File, doc: AppDocument | Doc) => {
        await handleUploadDoc(file, doc);
        setDocForPDFViewer({ ...doc });
    };

    const handleDeleteDoc = async (document: AppDocument[]) => {
        try {
            await dispatch(deleteDocs(document));
            if (activeApp) {
                dispatch(loadDocs(activeApp.id.toString()));
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleOpenCloseDeleteDocModal = () => {
        setDeleteDocConfirmationModalOpenClose((state) => !state);
    };

    const formatStatus = (status: string) => {
        const arrayStatus = status.split('_');
        return arrayStatus.join(' ').toLowerCase();
    };

    const handleTableSelection = (selection: TableSelection) => {
        setDocumentTableSelection(selection);
    };

    const handlePreviewDocumentFile = async (
        document: ClientDocument,
        previewType: 'sample' | 'template'
    ) => {
        // "Template from URL" docs have no uploaded file — open the linked template directly.
        if (previewType === 'template' && !document.template_uri) {
            const url = getExplanationUrl(document, language);
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
                return;
            }
        }

        try {
            const response =
                previewType === 'template'
                    ? await api.application.getDocumentTemplateUrl(
                          String(document.application_id),
                          document.id
                      )
                    : await api.application.getDocumentSampleUrl(
                          String(document.application_id),
                          document.id
                      );

            setPreviewFile({
                fileUrl: response.data.url,
                fileName: `${document.document_name} ${
                    previewType === 'template' ? 'Template' : 'Sample'
                }`,
                title: `${previewType === 'template' ? 'Template' : 'Sample'} preview`,
            });
        } catch {
            setAlertMessage({ type: 'error', value: t('somethingWentWrong', { lng: language }) });
            setShowingAlert(true);
        }
    };

    const docsGridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'minmax(260px, 1fr) 220px 120px 120px 130px',
        alignItems: 'center',
        columnGap: '0px',
    };

    return (
        <>
            <DocumentTableSelector
                selection={documentTableSelection}
                handleSelection={handleTableSelection}
                isPostItaDocs={postItaDocs.length > 0}
                isPostSubmissionDocs={postSubmissionDocs.length > 0}
            />
            <S.ListTable>
                <LTHeader innerStyle={mobile ? undefined : docsGridStyle}>
                    <LTItem style={{ width: '100%' }}>{t('name', { lng: language })}</LTItem>
                    <LTItem style={{ width: '100%', textAlign: 'left' }}>
                        {t('status', { lng: language })}
                    </LTItem>
                    <LTItem style={{ width: '100%', textAlign: 'center' }}>Sample</LTItem>

                    <LTItem style={{ width: '100%', textAlign: 'center' }}>
                        {t('template', { lng: language })}
                    </LTItem>
                    <LTItem style={{ width: '100%', textAlign: 'center' }}>
                        {t('actions', { lng: language })}
                    </LTItem>
                </LTHeader>
                {!isDocsLoading && docsList.length === 0 && (
                    <LTPlaceholder> {t('theListIsEmpty', { lng: language })}</LTPlaceholder>
                )}
                {!isDocsLoading && docsList.length > 0 && (
                    <>
                        {docsForTable.map((document, i) => (
                            <S.GreyOnHover
                                key={document.id}
                                id={String(document.id)}
                                style={i % 2 === 0 ? {} : { background: Colors.darkTableRow }}
                            >
                                <CustomLTRow wrapperStyle={mobile ? undefined : docsGridStyle}>
                                    {/*FULL SCREEN VERSION*/}
                                    {document.resource_name ? (
                                        <LTItem
                                            tip={document.document_name}
                                            style={{ width: '100%' }}
                                        >
                                            <S.InfoIconRow mobile={false}>
                                                <S.FakeLink
                                                    onClick={() => handleDisplayPDFViewer(document)}
                                                >
                                                    {document.document_name}
                                                </S.FakeLink>
                                                <DocumentExplanationPopover
                                                    explanation={
                                                        document.explanation
                                                            ? document.explanation
                                                            : '--'
                                                    }
                                                />
                                            </S.InfoIconRow>
                                        </LTItem>
                                    ) : (
                                        <LTItem style={{ width: '100%' }}>
                                            <S.InfoIconRow mobile={false}>
                                                {document.document_name}
                                                <DocumentExplanationPopover
                                                    explanation={
                                                        document.explanation
                                                            ? document.explanation
                                                            : '--'
                                                    }
                                                />
                                            </S.InfoIconRow>
                                        </LTItem>
                                    )}

                                    <LTItem
                                        style={{ width: '100%', textAlign: 'left' }}
                                        tip={formatStatus(document.status)}
                                    >
                                        {getCurrentStatus(document.status)}
                                    </LTItem>
                                    <LTItem style={{ width: '100%', textAlign: 'center' }}>
                                        {document.sample_uri ? (
                                            <S.Button
                                                onClick={() =>
                                                    handlePreviewDocumentFile(document, 'sample')
                                                }
                                                style={{ margin: '0 auto' }}
                                                title="Preview"
                                            >
                                                <Visibility fontSize="small" />
                                            </S.Button>
                                        ) : (
                                            '---'
                                        )}
                                    </LTItem>
                                    <LTItem style={{ width: '100%', textAlign: 'center' }}>
                                        {(() => {
                                            const templateUrl = document.template_uri
                                                ? null
                                                : getExplanationUrl(document, language);
                                            if (!document.template_uri && !templateUrl) {
                                                return '---';
                                            }
                                            return (
                                                <S.Button
                                                    onClick={() =>
                                                        handlePreviewDocumentFile(
                                                            document,
                                                            'template'
                                                        )
                                                    }
                                                    style={{ margin: '0 auto' }}
                                                    title={templateUrl ? 'Open link' : 'Preview'}
                                                >
                                                    {templateUrl ? (
                                                        <OpenInNew fontSize="small" />
                                                    ) : (
                                                        <Visibility fontSize="small" />
                                                    )}
                                                </S.Button>
                                            );
                                        })()}
                                    </LTItem>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '10px',
                                        }}
                                    >
                                        {activeApp?.client_status !==
                                            ClientApplicationStatus.CLIENT_WITH_ISSUES &&
                                        document.status !== AppDocStatus.MISSING &&
                                        document.status !== AppDocStatus.APPROVED &&
                                        document.status !== AppDocStatus.PREAPPROVED ? (
                                            <LTItem tip={t('deleteDocument', { lng: language })}>
                                                <IconBtn
                                                    onClick={() => {
                                                        setDocumentToDelete(document);
                                                        handleOpenCloseDeleteDocModal();
                                                    }}
                                                    style={{
                                                        padding: '0px',
                                                    }}
                                                >
                                                    <SvgIcon
                                                        style={{
                                                            width: '15',
                                                            height: '18px',
                                                            cursor: 'pointer',
                                                            fontSize: 'unset',
                                                        }}
                                                        viewBox="3 5 18 16.7"
                                                        component={BiTrash}
                                                    />
                                                </IconBtn>
                                            </LTItem>
                                        ) : activeApp?.client_status ===
                                          ClientApplicationStatus.CLIENT_WITH_ISSUES ? (
                                            <></>
                                        ) : (
                                            <LTItem maxWidth="16px" minWidth="16px">
                                                <></>
                                            </LTItem>
                                        )}
                                        {!!document.fields_json && (
                                            <LTItem
                                                tip={t('fillDocument', { lng: language })}
                                                style={{ height: '16.5px' }}
                                            >
                                                <FormBuilder docId={document.id} />
                                            </LTItem>
                                        )}
                                        <LTItem
                                            tip={t('downloadDocument', { lng: language })}
                                            style={{ marginTop: '3px' }}
                                        >
                                            <S.Button
                                                onClick={() => handleDownloadDoc(document)}
                                                disabled={
                                                    document.resource_name === '' ||
                                                    document.resource_name === null
                                                }
                                            >
                                                <DownloadIcon fill="#282828" />
                                            </S.Button>
                                        </LTItem>
                                        {activeApp?.client_status !==
                                            ClientApplicationStatus.CLIENT_WITH_ISSUES && (
                                            <LTItem
                                                tip={t('uploadDocument', { lng: language })}
                                                style={{ marginBottom: '3px' }}
                                            >
                                                <FilePicker
                                                    accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,.heic,.heif,image/png,image/jpeg,.png,.jpg,.jpeg"
                                                    getFile={(file) =>
                                                        handleUploadDoc(file, document)
                                                    }
                                                >
                                                    <IconBtn style={{ padding: '0px' }}>
                                                        <UploadIcon
                                                            fill="#282828"
                                                            style={{
                                                                transform: 'scale(1.1)',
                                                            }}
                                                        />
                                                    </IconBtn>
                                                </FilePicker>
                                            </LTItem>
                                        )}
                                    </div>

                                    {/*SMALL SCREEN VERSION*/}
                                    {document.resource_name ? (
                                        <LTMobileItem name={`${t('document', { lng: language })}`}>
                                            <S.InfoIconRow mobile={true}>
                                                <S.FakeLink
                                                    onClick={() => handleDisplayPDFViewer(document)}
                                                >
                                                    {document.document_name}
                                                </S.FakeLink>
                                                <DocumentExplanationPopover
                                                    explanation={
                                                        document.explanation
                                                            ? document.explanation
                                                            : '--'
                                                    }
                                                />
                                            </S.InfoIconRow>
                                        </LTMobileItem>
                                    ) : (
                                        <LTMobileItem name={`${t('document', { lng: language })}`}>
                                            <S.InfoIconRow mobile={true}>
                                                {document.document_name}
                                                <DocumentExplanationPopover
                                                    explanation={
                                                        document.explanation
                                                            ? document.explanation
                                                            : '--'
                                                    }
                                                />
                                            </S.InfoIconRow>
                                        </LTMobileItem>
                                    )}
                                    <LTMobileItem name={`${t('status', { lng: language })}`}>
                                        {getCurrentStatus(document.status)}
                                    </LTMobileItem>
                                    <LTMobileItem name="Sample">
                                        {document.sample_uri ? (
                                            <S.Button
                                                onClick={() =>
                                                    handlePreviewDocumentFile(document, 'sample')
                                                }
                                                style={{ margin: '0 auto' }}
                                                title="Preview"
                                            >
                                                <Visibility fontSize="small" />
                                            </S.Button>
                                        ) : (
                                            '---'
                                        )}
                                    </LTMobileItem>

                                    <LTMobileItem name={t('template', { lng: language })}>
                                        {(() => {
                                            const templateUrl = document.template_uri
                                                ? null
                                                : getExplanationUrl(document, language);
                                            if (!document.template_uri && !templateUrl) {
                                                return '---';
                                            }
                                            return (
                                                <S.Button
                                                    onClick={() =>
                                                        handlePreviewDocumentFile(
                                                            document,
                                                            'template'
                                                        )
                                                    }
                                                    style={{ margin: '0 auto' }}
                                                    title={templateUrl ? 'Open link' : 'Preview'}
                                                >
                                                    {templateUrl ? (
                                                        <OpenInNew fontSize="small" />
                                                    ) : (
                                                        <Visibility fontSize="small" />
                                                    )}
                                                </S.Button>
                                            );
                                        })()}
                                    </LTMobileItem>
                                    <LTMobileItem name={`${t('actions', { lng: language })}`}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                gap: '3px',
                                            }}
                                        >
                                            {activeApp?.client_status !==
                                                ClientApplicationStatus.CLIENT_WITH_ISSUES &&
                                                document.status !== AppDocStatus.MISSING &&
                                                document.status !== AppDocStatus.APPROVED &&
                                                document.status !== AppDocStatus.PREAPPROVED && (
                                                    <Tooltip title="Delete Document">
                                                        <div className="this div makes the tooltip work">
                                                            <IconBtn
                                                                onClick={() => {
                                                                    setDocumentToDelete(document);
                                                                    handleOpenCloseDeleteDocModal();
                                                                }}
                                                                style={{
                                                                    padding: '0px',
                                                                }}
                                                            >
                                                                <SvgIcon
                                                                    style={{
                                                                        width: '15',
                                                                        height: '18.7px',
                                                                        cursor: 'pointer',
                                                                        fontSize: 'unset',
                                                                    }}
                                                                    viewBox="2 3 20 14"
                                                                    component={BiTrash}
                                                                />
                                                            </IconBtn>
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            {!!document.fields_json && (
                                                <S.Button
                                                    style={{
                                                        maxWidth: '16px',
                                                        minWidth: '16px',
                                                        height: '16px',
                                                    }}
                                                >
                                                    <FormBuilder docId={document.id} />
                                                </S.Button>
                                            )}
                                            <S.Button
                                                onClick={() => handleDownloadDoc(document)}
                                                disabled={
                                                    document.resource_name === '' ||
                                                    document.resource_name === null
                                                }
                                            >
                                                <DownloadIcon fill="#282828" />
                                            </S.Button>

                                            {activeApp?.client_status !==
                                                ClientApplicationStatus.CLIENT_WITH_ISSUES && (
                                                <FilePicker
                                                    accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,.heic,.heif,image/png,image/jpeg,.png,.jpg,.jpeg"
                                                    getFile={(file) =>
                                                        handleUploadDoc(file, document)
                                                    }
                                                >
                                                    <IconBtn
                                                        style={{
                                                            padding: '0px',
                                                        }}
                                                    >
                                                        <UploadIcon
                                                            fill="#282828"
                                                            style={{
                                                                width: '22px',
                                                                height: '17px',
                                                            }}
                                                        />
                                                    </IconBtn>
                                                </FilePicker>
                                            )}
                                        </div>
                                    </LTMobileItem>
                                </CustomLTRow>
                            </S.GreyOnHover>
                        ))}
                    </>
                )}
                {isDocsLoading &&
                    skeletonArray.map((_, idx) => (
                        <LTRow key={idx}>
                            <LTItem minWidth="50%">
                                <Skeleton width={135} />
                            </LTItem>
                            <LTItem minWidth="35%">
                                <Skeleton width={130} />
                            </LTItem>
                            <LTItem minWidth="15%" lastItem>
                                <Skeleton height={36} width={150} />
                            </LTItem>
                        </LTRow>
                    ))}
            </S.ListTable>
            <AlertMessage
                type={alertMessage?.type}
                text={alertMessage?.value}
                open={isShowingAlert}
                onClose={() => setShowingAlert(false)}
            />
            <Modal
                sx={{ zIndex: '10000' }}
                open={isDocViewerOpen}
                onClose={openCloseDocViewerModal}
            >
                <S.ModalWrapper>
                    <S.Modal>
                        <S.ButtonWrapper>
                            <IconBtn
                                style={{ height: '24px', width: '24px' }}
                                onClick={openCloseDocViewerModal}
                            >
                                <CrossSimpleIcon style={{ fill: '#282828' }} />
                            </IconBtn>
                        </S.ButtonWrapper>
                        <DocumentPreview
                            doc={docForPDFViewer}
                            downloadDoc={handleDownloadDoc}
                            uploadDoc={handleReuploadDoc}
                        />
                    </S.Modal>
                </S.ModalWrapper>
            </Modal>
            <UrlDocumentPreview
                open={Boolean(previewFile)}
                mobile={mobile}
                onClose={() => setPreviewFile(null)}
                title={previewFile?.title}
                fileUrl={previewFile?.fileUrl}
                fileName={previewFile?.fileName}
            />

            <DeleteDocumentConfirmationModal
                toggleDeleteDocModal={handleOpenCloseDeleteDocModal}
                handleDeleteDoc={handleDeleteDoc}
                document={documentToDelete}
                open={deleteDocConfirmationModalOpen}
                mobile={mobile}
            />
        </>
    );
}
